import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import api from '@/admin/lib/api'
import { SEND_PERMISSION_BY_KIND } from '@/admin/lib/permissions'
import { useOrg } from '@/admin/hooks/use-org'
import { usePermissions } from '@/admin/hooks/use-permissions'
import { ImageUpload } from '@/admin/components/image-upload'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'

import { toastApiError } from '@/shared/lib/api-error'
interface EmailConfig {
  logo: number | null
  logo_uuid: string | null
  logo_url: string | null
  website_url: string
  facebook_url: string
  instagram_url: string
  twitter_url: string
  linkedin_url: string
  youtube_url: string
  support_email: string
  contact_email: string
  product_registration_branding: string
  organisation_invitation_branding: string
  partnership_invitation_branding: string
  magic_link_branding: string
  password_reset_branding: string
}

type BrandingField =
  | 'product_registration_branding'
  | 'organisation_invitation_branding'
  | 'partnership_invitation_branding'
  | 'magic_link_branding'
  | 'password_reset_branding'

type SocialField =
  | 'website_url'
  | 'facebook_url'
  | 'instagram_url'
  | 'twitter_url'
  | 'linkedin_url'
  | 'youtube_url'

const BRANDING_OPTIONS = [
  { value: 'GENERIC', label: 'iWarranty branded' },
  { value: 'COBRANDED', label: 'Co-branded' },
  { value: 'BRANDED', label: 'Fully branded' },
  { value: 'DISABLED', label: 'Disabled' },
]

const EMAIL_TYPES: Array<{ field: BrandingField; label: string }> = [
  { field: 'product_registration_branding', label: 'Product registration' },
  { field: 'organisation_invitation_branding', label: 'Team invitation' },
  { field: 'partnership_invitation_branding', label: 'Partnership invitation' },
  { field: 'magic_link_branding', label: 'Consumer magic link' },
  { field: 'password_reset_branding', label: 'Password reset' },
]

const SOCIAL_LINK_FIELDS: Array<{ field: SocialField; label: string; placeholder: string }> = [
  { field: 'website_url', label: 'Website', placeholder: 'https://example.com' },
  { field: 'facebook_url', label: 'Facebook', placeholder: 'https://facebook.com/...' },
  { field: 'instagram_url', label: 'Instagram', placeholder: 'https://instagram.com/...' },
  { field: 'twitter_url', label: 'X (Twitter)', placeholder: 'https://x.com/...' },
  { field: 'linkedin_url', label: 'LinkedIn', placeholder: 'https://linkedin.com/company/...' },
  { field: 'youtube_url', label: 'YouTube', placeholder: 'https://youtube.com/...' },
]

interface PlatformBranding {
  consumer_branding: string
  admin_branding: string
}

const PLATFORM_TYPES: Array<{ field: keyof PlatformBranding; label: string; description: string }> = [
  { field: 'consumer_branding', label: 'Consumer app (Olive)', description: 'Login page, header, product cards' },
  { field: 'admin_branding', label: 'Admin app (Sage)', description: 'App header and navigation' },
]

/** When the mailroom gets its letters, as the settings endpoint reports it. */
interface MailBatchState {
  schedule: 'daily' | 'each'
  next_batch_at: string | null
  timezone: string
  waiting: number
}

function mailBatchFrom(data: unknown): MailBatchState | null {
  const d = (data ?? {}) as Partial<MailBatchState>
  if (d.schedule !== 'daily' && d.schedule !== 'each') return null
  return {
    schedule: d.schedule,
    next_batch_at: typeof d.next_batch_at === 'string' ? d.next_batch_at : null,
    timezone: typeof d.timezone === 'string' ? d.timezone : 'UTC',
    waiting: typeof d.waiting === 'number' ? d.waiting : 0,
  }
}

/** The next batch in the mailroom's own time, e.g. "Wed, Oct 28, 5:00 PM EDT". */
function batchTimeLabel(state: MailBatchState): string {
  if (!state.next_batch_at) return ''
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeZone: state.timezone,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    }).format(new Date(state.next_batch_at))
  } catch {
    return new Date(state.next_batch_at).toLocaleString()
  }
}

export function CompanyTab() {
  const { t } = useTranslation()
  const { orgUuid, orgName } = useOrg()
  const { can } = usePermissions()
  const queryClient = useQueryClient()
  const [config, setConfig] = useState<EmailConfig | null>(null)
  // Where letters sent by post are emailed to print; null when not readable.
  const [mailroom, setMailroom] = useState<string | null>(null)
  const [savedMailroom, setSavedMailroom] = useState('')
  // When the mailroom gets its letters, and how many are waiting (WPS-231).
  const [mailBatch, setMailBatch] = useState<MailBatchState | null>(null)
  const [sendingBatch, setSendingBatch] = useState(false)
  const [platformBranding, setPlatformBranding] = useState<PlatformBranding | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (orgUuid) fetchConfig()
  }, [orgUuid]) // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchConfig() {
    try {
      const res = await api.get(`/organisations/${orgUuid}/email-config/`)
      setConfig(res.data)
    } catch (err) {
      toastApiError(err, 'Failed to load organisation settings');
    }
    try {
      const res = await api.get(`/organisations/${orgUuid}/platform-branding/`)
      setPlatformBranding(res.data)
    } catch (err) {
      console.error('[platform-branding] fetch failed:', err)
    }
    try {
      const res = await api.get('/organisations/mailroom/')
      const value = res.data?.mailroom_email ?? ''
      setMailroom(value)
      setSavedMailroom(value)
      setMailBatch(mailBatchFrom(res.data))
    } catch (err) {
      console.error('[mailroom] fetch failed:', err)
    }
    setLoading(false)
  }

  async function sendMailBatchNow() {
    setSendingBatch(true)
    try {
      const res = await api.post('/organisations/mailroom/send-now/')
      setMailBatch(mailBatchFrom(res.data))
      const sent = Number(res.data?.sent ?? 0)
      toast.success(
        sent
          ? t('companySettings.mailBatchSent', '{{count}} letter(s) sent to the mailroom.', {
              count: sent,
            })
          : t('companySettings.mailBatchNothing', 'No letters are waiting.'),
      )
    } catch (err) {
      toastApiError(err, t('companySettings.mailBatchFailed', 'The letters could not be sent.'))
    } finally {
      setSendingBatch(false)
    }
  }

  async function handleSave() {
    if (!config || !orgUuid) return
    setSaving(true)
    try {
      const mailroomChanged = mailroom !== null && mailroom.trim() !== savedMailroom
      const [emailRes, , mailroomRes] = await Promise.all([
        api.put(`/organisations/${orgUuid}/email-config/`, config),
        platformBranding
          ? api.put(`/organisations/${orgUuid}/platform-branding/`, platformBranding)
          : Promise.resolve(null),
        mailroomChanged
          ? api.put('/organisations/mailroom/', { mailroom_email: mailroom.trim() })
          : Promise.resolve(null),
      ])
      setConfig(emailRes.data)
      if (mailroomRes) {
        const value = mailroomRes.data?.mailroom_email ?? ''
        setMailroom(value)
        setSavedMailroom(value)
        setMailBatch(mailBatchFrom(mailroomRes.data))
      }
      // Refresh org context so the header logo updates
      await queryClient.invalidateQueries({ queryKey: ['org-context'] })
      toast.success('Organisation settings saved')
    } catch (err) {
      toastApiError(err, 'Failed to save organisation settings');
    } finally {
      setSaving(false)
    }
  }

  function updateField(field: string, value: string | number | null) {
    if (!config) return
    setConfig({ ...config, [field]: value })
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-[var(--ink-3)]" />
      </div>
    )
  }

  if (!config) return null

  const hasLogo = !!config.logo_url

  return (
    <div className="space-y-8 max-w-lg">
      {/* Organisation name */}
      <div>
        <h3 className="clara-section-label mb-5 border-b-[0.5px] border-b-[var(--rule)] pb-3">
          Organisation details
        </h3>
        <div className="mb-4">
          <Label className="clara-section-label mb-1.5 block">Organisation name</Label>
          <p className="text-[13px] text-[var(--ink)]">
            {orgName}
          </p>
        </div>
      </div>
      {/* Logo */}
      <div>
        <h3 className="clara-section-label mb-5 border-b-[0.5px] border-b-[var(--rule)] pb-3">
          Logo
        </h3>
        <ImageUpload
          value={config.logo ? String(config.logo) : null}
          previewUrl={config.logo_url}
          useCase="ORGANISATION_LOGO"
          label="Organisation logo"
          onChange={(uuid, file) => {
            updateField('logo_uuid', uuid ?? null)
            if (file) {
              setConfig((prev) =>
                prev ? { ...prev, logo_uuid: uuid, logo_url: file.file_url } : prev
              )
            } else {
              setConfig((prev) => (prev ? { ...prev, logo: null, logo_uuid: null, logo_url: null } : prev))
            }
          }}
        />
        <p className="mt-1.5 text-[11px] text-[var(--ink-3)]">
          Used in co-branded and fully branded emails. Recommended: PNG or JPG, max 15 MB.
        </p>
      </div>
      {/* Social links */}
      <div>
        <h3 className="clara-section-label mb-5 border-b-[0.5px] border-b-[var(--rule)] pb-3">
          Social links
        </h3>
        <div className="space-y-3">
          {SOCIAL_LINK_FIELDS.map(({ field, label, placeholder }) => (
            <div key={field}>
              <Label className="clara-section-label mb-1.5 block">{label}</Label>
              <Input
                value={config[field] || ''}
                onChange={(e) => updateField(field, e.target.value)}
                placeholder={placeholder}
                className="text-[13px]"
              />
            </div>
          ))}
        </div>
      </div>
      {/* Contact emails */}
      <div>
        <h3 className="clara-section-label mb-5 border-b-[0.5px] border-b-[var(--rule)] pb-3">
          Contact details
        </h3>
        <div className="space-y-3">
          <div>
            <Label className="clara-section-label mb-1.5 block">Support email</Label>
            <Input
              data-testid="organisation-support-email"
              value={config.support_email}
              onChange={(e) => updateField('support_email', e.target.value)}
              placeholder="support@example.com"
              className="text-[13px]"
              type="email"
            />
            <p className="mt-1 text-[11px] text-[var(--ink-3)]">
              Shown in fully branded email footers.
            </p>
          </div>
          <div>
            <Label className="clara-section-label mb-1.5 block">Contact email</Label>
            <Input
              data-testid="organisation-contact-email"
              value={config.contact_email}
              onChange={(e) => updateField('contact_email', e.target.value)}
              placeholder="hello@example.com"
              className="text-[13px]"
              type="email"
            />
          </div>
        </div>
      </div>
      {/* Letters by post */}
      {mailroom !== null && (
        <div>
          <h3 className="clara-section-label mb-5 border-b-[0.5px] border-b-[var(--rule)] pb-3">
            {t('companySettings.lettersByPost', 'Letters by post')}
          </h3>
          <Label className="clara-section-label mb-1.5 block">
            {t('companySettings.mailroomInbox', 'Mailroom inbox')}
          </Label>
          <Input
            data-testid="organisation-mailroom-email"
            value={mailroom}
            onChange={(e) => setMailroom(e.target.value)}
            placeholder="mailroom@example.com"
            className="text-[13px]"
            type="email"
          />
          <p className="mt-1 text-[11px] text-[var(--ink-3)]">
            {t(
              'companySettings.mailroomHint',
              'Letters sent by Mail go here to print and post. Leave it empty to send letters by email only.',
            )}
          </p>
          {mailBatch && savedMailroom && (
            <div
              className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] px-4 py-3"
              data-testid="organisation-mail-batch"
            >
              <p className="min-w-0 flex-1 text-[12px] text-[var(--ink-2)]">
                {mailBatch.schedule === 'daily'
                  ? t(
                      'companySettings.mailBatchDaily',
                      'The mailroom gets every letter to post once a day, as one PDF ready to print. Next batch: {{when}}. Waiting now: {{count}}.',
                      { when: batchTimeLabel(mailBatch), count: mailBatch.waiting },
                    )
                  : t(
                      'companySettings.mailBatchEach',
                      'Each letter goes to the mailroom as soon as it is sent.',
                    )}
              </p>
              {/* Sending the batch is sending letters: shown only to those who may. */}
              {mailBatch.schedule === 'daily' && can(SEND_PERMISSION_BY_KIND.letter) && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={sendingBatch}
                  onClick={sendMailBatchNow}
                  data-testid="organisation-mail-batch-send-now"
                >
                  {sendingBatch ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
                  {t('companySettings.mailBatchSendNow', 'Send waiting letters now')}
                </Button>
              )}
            </div>
          )}
        </div>
      )}
      {/* Email branding */}
      <div>
        <h3 className="clara-section-label mb-5 border-b-[0.5px] border-b-[var(--rule)] pb-3">
          Email branding
        </h3>
        <p className="mb-4 text-[11px] text-[var(--ink-3)]">
          Choose how each email type is branded. Co-branded and fully branded options require a logo.
        </p>
        <div className="space-y-3">
          {EMAIL_TYPES.map(({ field, label }) => (
            <div key={field} className="flex items-center justify-between gap-4">
              <Label className="shrink-0 text-[13px] text-[var(--ink)]">
                {label}
              </Label>
              <Select
                value={config[field]}
                onValueChange={(val) => updateField(field, val as string)}
              >
                <SelectTrigger className="min-w-[180px] text-[12px]">
                  <SelectValue>
                    {BRANDING_OPTIONS.find((o) => o.value === config[field])?.label ?? 'Select...'}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {BRANDING_OPTIONS.map((opt) => (
                    <SelectItem
                      key={opt.value}
                      value={opt.value}
                      disabled={
                        !hasLogo && (opt.value === 'COBRANDED' || opt.value === 'BRANDED')
                      }
                    >
                      {opt.label}
                      {!hasLogo && (opt.value === 'COBRANDED' || opt.value === 'BRANDED')
                        ? ' (requires logo)'
                        : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
      </div>
      {/* Platform branding */}
      {platformBranding && (
        <div>
          <h3 className="clara-section-label mb-5 border-b-[0.5px] border-b-[var(--rule)] pb-3">
            Platform branding
          </h3>
          <p className="mb-4 text-[11px] text-[var(--ink-3)]">
            Choose how each app surface is branded. Co-branded and fully branded options require a logo.
          </p>
          <div className="space-y-3">
            {PLATFORM_TYPES.map(({ field, label, description }) => (
              <div key={field} className="flex items-center justify-between gap-4">
                <div className="shrink-0">
                  <Label className="text-[13px] text-[var(--ink)]">
                    {label}
                  </Label>
                  <p className="text-[10px] text-[var(--ink-3)]">{description}</p>
                </div>
                <Select
                  value={platformBranding[field]}
                  onValueChange={(val) =>
                    setPlatformBranding({ ...platformBranding, [field]: val })
                  }
                >
                  <SelectTrigger className="min-w-[180px] text-[12px]">
                    <SelectValue>
                      {BRANDING_OPTIONS.find((o) => o.value === platformBranding[field])?.label ?? 'Select...'}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {BRANDING_OPTIONS.map((opt) => (
                      <SelectItem
                        key={opt.value}
                        value={opt.value}
                        disabled={
                          !hasLogo && (opt.value === 'COBRANDED' || opt.value === 'BRANDED')
                        }
                      >
                        {opt.label}
                        {!hasLogo && (opt.value === 'COBRANDED' || opt.value === 'BRANDED')
                          ? ' (requires logo)'
                          : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Save - the email config and branding endpoints need organisation.edit. */}
      {can('organisation.edit') && (
        <div className="flex justify-end border-t-[0.5px] border-t-[var(--rule)] pt-5">
          <Button
            data-testid="organisation-save-btn"
            onClick={handleSave}
            disabled={saving}
            className="h-auto rounded-[4px] bg-[var(--green)] px-5 py-2 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] hover:bg-[var(--green-hover)] disabled:opacity-40"
          >
            {saving ? 'Saving...' : 'Save changes'}
          </Button>
        </div>
      )}
    </div>
  );
}
